import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './reservationsCreatedTable.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import ReservationsCreatedTableRow from './ReservationsCreatedTableRow';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';
import { getPerformanceReservationsCreatedTable } from '@/store/slices/components/dashboard/service';

export default function ReservationsCreatedTable() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { reservationsCreated } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Property'),
      subtitle: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.PROPERTY_ADDRESS,
    },
    {
      id: 2,
      title: Trans(translations, 'Operation'),
      subtitle: Trans(translations, 'Type'),
      field: TABLE_SORT_FIELDS.OPERATION,
    },
    {
      id: 3,
      title: Trans(translations, 'Reserve'),
      subtitle: Trans(translations, 'Operation'),
      field: TABLE_SORT_FIELDS.RESERVATION_VALUE,
    },
    {
      id: 4,
      title: Trans(translations, 'Contact'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 5,
      title: Trans(translations, 'Agent'),
      subtitle: Trans(translations, 'Branch'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 6,
      title: Trans(translations, 'Status'),
      field: TABLE_SORT_FIELDS.STATUS,
    },
    {
      id: 7,
      title: Trans(translations, 'Creation'),
      field: TABLE_SORT_FIELDS.CREATED_AT,
    },
    {
      id: 8,
      title: Trans(translations, 'Expiration'),
      field: TABLE_SORT_FIELDS.EXPIRATION_DATE,
    },
  ];

  useEffect(() => {
    if (!reservationsCreated.has_next) return;
    if (!isInViewport || reservationsCreated.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(reservationsCreated, {
      withNextPage: true,
    });

    dispatch(getPerformanceReservationsCreatedTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    if (!header.field) return;

    dispatch(
      setPerformanceModals({
        key: 'reservationsCreated',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.reservationsCreated(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="reservations-created-table"
      className={`${parentStyles.table} ${specificStyles.reservationsCreatedTable}`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={reservationsCreated.sortBy}
            sortDirection={reservationsCreated.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {reservationsCreated.list.map((row) => (
          <ReservationsCreatedTableRow key={row.id} row={row} />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(reservationsCreated.loading || reservationsCreated.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
